import { useEffect, useState } from 'react';
import api from '../../api/client';
import { OrgSelector } from '../shared/OrgSelector';
import { useAuthStore } from '../../stores/authStore';
import { useOrganizationStore } from '../../stores/organizationStore';
import { listOrganizations } from '../../api/organizations';
import { getAssignableRoles } from '../../utils/rbac';
import styles from './AdminPage.module.css';

interface UserRecord {
  id: string;
  email: string;
  full_name: string;
  role: string;
  organization_id: string | null;
  is_active: boolean;
  created_at: string;
}

export function UsersManagement() {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'analyst',
    organization_id: null as string | null,
  });

  const user = useAuthStore((s) => s.user);
  const organizations = useOrganizationStore((s) => s.organizations);
  const setOrganizations = useOrganizationStore((s) => s.setOrganizations);

  // Determine admin's org level
  const adminOrg = organizations.find((o) => o.id === user?.organization_id);
  const adminOrgLevel = adminOrg?.level ?? null;
  const assignableRoles = getAssignableRoles(user?.role ?? 'viewer', adminOrgLevel);

  useEffect(() => {
    loadUsers();
    if (organizations.length === 0) {
      listOrganizations('flat').then(setOrganizations).catch(() => {});
    }
  }, []);

  async function loadUsers() {
    try {
      setLoading(true);
      // Backend `/users` returns a paginated envelope: { items, total, page, limit, pages }.
      // Accepting both shapes defensively in case some code paths still return a bare array.
      const res = await api.get<{ items: UserRecord[] } | UserRecord[]>('/users', {
        params: { limit: 200 },
      });
      const payload = res.data;
      const list = Array.isArray(payload) ? payload : (payload.items ?? []);
      setUsers(list);
    } catch (err) {
      console.error('[UsersManagement] failed to load users:', err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    try {
      await api.post('/users', formData);
      setShowForm(false);
      setFormData({ email: '', password: '', full_name: '', role: 'analyst', organization_id: null });
      await loadUsers();
    } catch (err) {
      console.error('[UsersManagement] request failed:', err);
    }
  }

  async function handleDeactivate(userId: string) {
    if (!confirm('Deactivate this user?')) return;
    try {
      await api.delete(`/users/${userId}`);
      await loadUsers();
    } catch (err) {
      console.error('[UsersManagement] request failed:', err);
    }
  }

  if (loading) return <div className={styles.empty}>Loading users...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className={styles.createBtn} onClick={() => setShowForm(!showForm)}>
          + New User
        </button>
      </div>

      {showForm && (
        <form className={styles.form} onSubmit={handleCreate}>
          <div className={styles.formRow}>
            <input
              className={styles.input}
              placeholder="Full name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              required
            />
            <input
              className={styles.input}
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>
          <div className={styles.formRow}>
            <input
              className={styles.input}
              type="password"
              placeholder="Password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
            <select
              className={styles.input}
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            >
              {assignableRoles.map((role) => (
                <option key={role} value={role}>
                  {role === 'tenant_admin' ? 'Admin' : role.charAt(0).toUpperCase() + role.slice(1)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, display: 'block' }}>
              Organization
            </label>
            <OrgSelector
              value={formData.organization_id}
              onChange={(id) => setFormData({ ...formData, organization_id: id })}
            />
          </div>
          <div className={styles.formActions}>
            <button type="button" className={styles.actionBtn} onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className={styles.createBtn}>Create User</button>
          </div>
        </form>
      )}

      {users.length === 0 ? (
        <div className={styles.empty}>No users found.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Active</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.full_name}</td>
                <td>{u.email}</td>
                <td>{u.role}</td>
                <td>{u.is_active ? 'Yes' : 'No'}</td>
                <td>
                  {u.is_active && (
                    <button
                      className={`${styles.actionBtn} ${styles.dangerBtn}`}
                      onClick={() => handleDeactivate(u.id)}
                    >
                      Deactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
